import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  decodeHtmlEntities,
  deleteUnnecessaryProperties,
  IListResponseData,
  SERVICES_API,
} from '@loreal-doctors-video-platform/data';
import { map, Observable } from 'rxjs';
import { ISpecializationDropdownDataItem } from '../_models/specialization-dropdown-data-item';
import { IWebinarListItem } from '../_models/webinar/webinar-list-item';
import { IWebinarDetails } from '../_models/webinar/webinar-details';
import { IWebinarDetailsForLoggedUser } from '../_models/webinar/webinar-details-for-logged-user';
import { Store } from '@ngxs/store';
import { environment } from '../../environments/environment';
import { AppState } from '../app.state';
import { ALLOWED_EMAILS_TO_ENTER_WEBINAR } from '../app.consts';
import { IWebinarCategory } from '../_models/webinar/webinar-category';
import { IWebinarListParams, WebinarListSort } from '../_models/webinar/webinar-list-params';

@Injectable({
  providedIn: 'root',
})
export class WebinarService {
  constructor(
    @Inject(SERVICES_API)
    private readonly _servicesApi: string,
    private readonly _httpClient: HttpClient,
    private readonly _store: Store,
  ) {}

  /**
   * Get list of client specializations
   */
  public fetchSpecializations(): Observable<ISpecializationDropdownDataItem[]> {
    return this._httpClient.get<ISpecializationDropdownDataItem[]>(
      `${this._servicesApi}/platform/client/specialization/dropdown`,
    );
  }

  /**
   * Get list of webinar categories for dropdown
   */
  public fetchCategories(): Observable<IWebinarCategory[]> {
    return this._httpClient.get<IWebinarCategory[]>(`${this._servicesApi}/platform/webinar/category/dropdown`);
  }

  /**
   * Get list of webinars
   */
  public fetchWebinarList(params: IWebinarListParams): Observable<IWebinarListItem[]> {
    const defaultParams: IWebinarListParams = {
      page: 1,
      perPage: 99999,
      sort: 'start_date' as WebinarListSort,
      direction: 'desc',
    };

    const mergedParams = deleteUnnecessaryProperties<IWebinarListParams>({
      ...defaultParams,
      ...params,
    });
    console.warn(mergedParams);
    return this._httpClient
      .get<IListResponseData<IWebinarListItem>>(`${this._servicesApi}/platform/webinar/list`, {
        params: { ...JSON.parse(JSON.stringify(mergedParams)) },
      })
      .pipe(
        map((response) => {
          // todo remove after tests
          if (environment.production) {
            const user = this._store.selectSnapshot(AppState.user);
            const hiddenWebinarIds = [9];
            return response.data.filter((webinar) => {
              if (user?.email && ALLOWED_EMAILS_TO_ENTER_WEBINAR.some((email) => user.email.includes(email))) {
                return true;
              }
              return !hiddenWebinarIds.includes(webinar.id);
            });
          }
          return response.data;
        }),
      );
  }

  /**
   * Get webinar details for logged client
   * @param slug
   */
  public findWebinarDetailsForLoggedClient(slug: string): Observable<IWebinarDetailsForLoggedUser> {
    return this._httpClient
      .get<IWebinarDetailsForLoggedUser>(`${this._servicesApi}/platform/client/webinar/${slug}/details`)
      .pipe(
        map((details) => ({
          ...details,
          webinar: WebinarService._decodePlainTextFields(details?.webinar),
        })),
      );
  }

  /**
   * Get webinar details by slug
   * @param slug
   */
  public findWebinarDetailsBySlug(slug: string): Observable<IWebinarDetails> {
    return this._httpClient
      .get<IWebinarDetails>(`${this._servicesApi}/platform/webinar/${slug}/details`)
      .pipe(map((webinar) => WebinarService._decodePlainTextFields(webinar)));
  }

  /**
   * Download webinar file by slug and file id
   * @param slug
   * @param fileId
   */
  public downloadFile(slug: string, fileId: number): Observable<Blob> {
    return this._httpClient.get<Blob>(
      `${this._servicesApi}/platform/client/webinar/${slug}/download/${fileId}/file-content`,
      {
        responseType: 'blob' as 'json',
      },
    );
  }

  /**
   * Download webinar certificate by slug
   * @param slug
   */
  public downloadCertificate(slug: string): Observable<Blob> {
    return this._httpClient.get<Blob>(`${this._servicesApi}/platform/client/webinar/${slug}/certificate`, {
      responseType: 'blob' as 'json',
    });
  }

  private static _decodePlainTextFields(webinar: IWebinarDetails): IWebinarDetails {
    return {
      ...webinar,
      title: decodeHtmlEntities(webinar?.title),
      schedules: webinar?.schedules
        ? webinar.schedules.map((schedule) => ({
            ...schedule,
            name: decodeHtmlEntities(schedule?.name),
            sub_name: decodeHtmlEntities(schedule?.sub_name),
            sub_name2: decodeHtmlEntities(schedule?.sub_name2),
          }))
        : [],
      videos: webinar?.videos
        ? webinar.videos.map((video) => ({
            ...video,
            name: decodeHtmlEntities(video?.name),
            sub_name: decodeHtmlEntities(video?.sub_name),
          }))
        : [],
    };
  }
}
